import {memo, useCallback, useMemo} from "react";
import {Logic, LogicProps} from "./CopilotContainer.interfaces";
import {useReducer} from "./CopilotContainer.reducer";
import {TextGeneratorLength, TextGeneratorTone} from "../../../../Interfaces/TextGenerator";
import languageObject from "../../../../translations";
import {ComponentEventHandler, TextAreaProps} from "@fluentui/react-northstar";
import {CopilotApi} from "../../../../apis/copilotApi";

const keepSameLogic = (prevProps: Readonly<LogicProps>, nextProps: Readonly<LogicProps>): boolean => {
    return (
        prevProps.locale === nextProps.locale
    );
}

export const CopilotContainerLogic = memo((props: LogicProps): JSX.Element => {
    const logic: Logic = useLogic(props);
    return props.children(logic);
}, keepSameLogic);

export const useLogic = (props: LogicProps) => {
    const {state, dispatch} = useReducer();

    const handleToggleOpen = useCallback(() => {
        dispatch({type: "setOpen", value: !state.open});
    }, []);

    const handleStartGeneration = useCallback((locale: string) => async () => {
        if (state.isGenerating) return;
        dispatch({type: "setIsGenerating", value: true});
        const generatedTextResult = await CopilotApi.generateMessage({
            prompt: state.instruction,
            style: state.tone,
            length: state.length,
            language: locale,
        });
        if (!generatedTextResult) return dispatch({type: "setIsGenerating", value: false});
        let isTitleWritten = false;
        let isMessageWritten = false;
        simulateWriting(generatedTextResult.title, props.onTitleGenerated).then(() => {
            isTitleWritten = true;
            if (isMessageWritten) {
                dispatch({type: "setIsGenerating", value: false});
                clearTimeout(timeout);
            }
        });
        const message = generatedTextResult.paragraphs.join("\r").slice(0, props.maxLength);
        simulateWriting(message, props.onMessageGenerated).then(() => {
            isMessageWritten = true;
            if (isTitleWritten) {
                dispatch({type: "setIsGenerating", value: false});
                clearTimeout(timeout);
            }
        });
        const timeout = setTimeout(() => {
            if (state.isGenerating) dispatch({type: "setIsGenerating", value: false});
        }, 30000);
    }, []);

    const handleChangeInstruction: ComponentEventHandler<TextAreaProps> = useCallback((e, data) => {
        const value = (data?.value ?? "") + "";
        if (value.length > 500) return;
        dispatch({type: "setInstruction", value});
    }, []);

    const availableTones = useMemo(() => (
        Object.keys(TextGeneratorTone).filter((key: string | number) => !isNaN(Number(key)))
            .map(key => Number(key) as TextGeneratorTone).map(t => ({
            key: t,
            content: languageObject.translate(TextGeneratorTone[t]),
            onClick: () => dispatch({type: "setTone", value: t})
        }))
    ), []);

    const availableLengths = useMemo(() => (
        Object.keys(TextGeneratorLength).filter((key: string | number) => !isNaN(Number(key)))
            .map(key => Number(key) as TextGeneratorLength).map(l => ({
            key: l,
            content: languageObject.translate(TextGeneratorLength[l]),
            onClick: () => dispatch({type: "setLength", value: l})
        }))
    ), []);

    const activeToneIndex = availableTones.findIndex(t => t.key === state.tone);

    const activeLengthIndex = availableLengths.findIndex(l => l.key === state.length);

    return {
        ...props,
        ...state,
        availableTones,
        availableLengths,
        activeToneIndex,
        activeLengthIndex,
        handleStartGeneration,
        handleToggleOpen,
        handleChangeInstruction,
    }
}

///////////////////////////////////////////////////// PURE METHODS /////////////////////////////////////////////////////

const simulateWriting = async (text: string, onWrite: (text: string) => void) => {
    if (text.length === 0) return;
    for (let i = 0; i < text.length; i++) {
        onWrite(text.slice(0, i + 1));
        const timeout = getRandomNumber(1, 20);
        await wait(timeout);
    }
}

const wait = async (timeout: number) => new Promise<void>(resolve => setTimeout(resolve, timeout));

const getRandomNumber = (min: number, max: number) => {
    return Math.floor(Math.random() * (max - min + 1)) + min;
}